import React, { useEffect, useState } from 'react';
import { logAuditAction } from '../utils/auditLogger';
import './styles/PanelCorrelaciones.css';
import mapboxgl from 'mapbox-gl';

const LAYER_ID  = 'layer-demo-relaciones';
const SOURCE_ID = 'line-demo-relaciones';

const PanelCorrelaciones = ({
  startDemoAnimation,
  hostsDisponibles,
  mapAux,
  setCapas,
  capas,
  demoRelaciones,
  setDemoRelaciones,
  CreateCisModalOpen,
}) => {
  const [hostA, setHostA]           = useState('');
  const [hostB, setHostB]           = useState('');
  const [isCollapsed, setIsCollapsed] = useState(true);

  /* ── Helpers ──────────────────────────────────────────────── */
  const calcularPuntoMedio = ([x1, y1], [x2, y2]) => [
    (x1 + x2) / 2,
    (y1 + y2) / 2,
  ];

  const parseCoord = val =>
    parseFloat(typeof val === 'string' ? val.replace(',', '.') : val);

  /* ── Capas del mapa ───────────────────────────────────────── */
  const handleCapaToggle = index => {
    const layer = capas[index]?.id;
    if (capas[index].show) {
      mapAux.removeLayer(layer);
      mapAux.removeSource(layer);
    } else {
      const keys = Object.keys(capas);
      let inserted = false;
      for (let i = index + 1; i < keys.length; i++) {
        if (capas[i].show) {
          if (capas[index].source) mapAux.addSource(layer, capas[index].source);
          mapAux.addLayer(capas[index].layer, capas[i].id);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        if (capas[index].source) mapAux.addSource(layer, capas[index].source);
        mapAux.addLayer(capas[index].layer);
      }
    }
    setCapas(prev => ({
      ...prev,
      [index]: { ...prev[index], show: !prev[index].show },
    }));
  };

  const handleRelacionesLayer = collapsed => {
    if (collapsed) {
      mapAux.removeLayer(LAYER_ID);
      mapAux.removeSource(SOURCE_ID);
      return;
    }

    mapAux.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: demoRelaciones },
    });

    mapAux.addLayer({
      id: LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': '#08b4ee',
        'line-width': 8,
        'line-dasharray': [0, 4, 3],
      },
    });

    mapAux.on('mouseleave', LAYER_ID, () => {
      mapAux.getCanvas().style.cursor = '';
      document.querySelectorAll('.custom-popup').forEach(p => p.remove());
    });

    mapAux.on('mouseenter', LAYER_ID, e => {
      mapAux.getCanvas().style.cursor = 'pointer';
      const feature     = e.features[0];
      const coordinates = calcularPuntoMedio(
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1],
      );
      const { label } = feature.properties;
      const getRandom  = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
      const throughput = `${getRandom(20, 90)} Mbps`;
      const dbm        = `-${getRandom(40, 70)} dBm`;

      new mapboxgl.Popup({ className: 'custom-popup', closeButton: false })
        .setLngLat(coordinates)
        .setHTML(`
          <div style="background:#1a1a1acc;color:white;padding:8px;border:1px solid #07b0ff;border-radius:4px;">
            <b style="display:block;border-bottom:1px solid #555;margin-bottom:5px;">${label || '—'}</b>
            <div style="font-size:11px;">Estado: <span style="color:#1fee08">Estable</span></div>
            <div style="font-size:11px;">Throughput: ${throughput}</div>
            <div style="font-size:11px;">Alineación: ${dbm}</div>
          </div>
        `)
        .addTo(mapAux);
    });

    startDemoAnimation(mapAux, LAYER_ID);
  };

  useEffect(() => {
    if (Object.keys(capas).length > 0) {
      handleRelacionesLayer(isCollapsed);
      handleCapaToggle(1);
      handleCapaToggle(2);
    }
  }, [isCollapsed]);

  /* ── CRUD de relaciones ───────────────────────────────────── */
  const crearRelacion = () => {
    if (!hostA || !hostB || hostA === hostB)
      return alert('Selecciona dos hosts distintos');

    const infoA = hostsDisponibles.find(h => h.hostid == hostA);
    const infoB = hostsDisponibles.find(h => h.hostid == hostB);

    const nuevaRelacion = {
      id: `${hostA}-${hostB}-${Date.now()}`,
      type: 'Feature',
      properties: {
        label: `${infoA.Host} ↔ ${infoB.Host}`,
        connectivity: 1,
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          [parseCoord(infoA.longitude), parseCoord(infoA.latitude)],
          [parseCoord(infoB.longitude), parseCoord(infoB.latitude)],
        ],
      },
    };

    setDemoRelaciones([...demoRelaciones, nuevaRelacion]);
    setHostA('');
    setHostB('');
    logAuditAction("Crear", `Correlación ${infoA.Host} ↔ ${infoB.Host}`, "Correlaciones");
  };

  const eliminarRelacion = id => {
    const rel = demoRelaciones.find(r => r.id === id);
    if(rel) {
      logAuditAction("Eliminar", `Correlación ${rel.properties.label}`, "Correlaciones");
    }
    setDemoRelaciones(demoRelaciones.filter(r => r.id !== id));
  };

  /* ── Render ───────────────────────────────────────────────── */
  if (CreateCisModalOpen) return null;

  return (
    <div className="pc-panel">

      {/* Header */}
      <div className="pc-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <span className="pc-title">Crear correlaciones</span>
        <div className="pc-header-right">
          {demoRelaciones.length > 0 && (
            <span className="pc-count-badge">{demoRelaciones.length}</span>
          )}
          <span className="pc-toggle">{isCollapsed ? '＋' : '－'}</span>
        </div>
      </div>

      {/* Hint siempre visible */}
      <div className="pc-hint">
        Al abrir el panel, las líneas existentes se ocultan para mejor visualización.
      </div>

      {/* Cuerpo colapsable */}
      {!isCollapsed && (
        <div className="pc-body">

          {/* Lista de relaciones creadas */}
          {demoRelaciones.length > 0 ? (
            <div className="pc-list">
              {demoRelaciones.map(rel => (
                <div key={rel.id} className="pc-item">
                  <span className="pc-item-label">{rel.properties.label}</span>
                  <button
                    className="pc-item-delete"
                    onClick={() => eliminarRelacion(rel.id)}
                    title="Eliminar"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="pc-empty">Sin correlaciones creadas aún.</p>
          )}

          {/* Formulario */}
          <div className="pc-form">
            <div className="pc-field">
              <label className="pc-label">Host origen</label>
              <select
                className="pc-select"
                value={hostA}
                onChange={e => setHostA(e.target.value)}
              >
                <option value="">Seleccionar host...</option>
                {hostsDisponibles.map(h => (
                  <option key={h.hostid} value={h.hostid}>{h.Host}</option>
                ))}
              </select>
            </div>

            <div className="pc-field">
              <label className="pc-label">Host destino</label>
              <select
                className="pc-select"
                value={hostB}
                onChange={e => setHostB(e.target.value)}
              >
                <option value="">Seleccionar host...</option>
                {hostsDisponibles.map(h => (
                  <option key={h.hostid} value={h.hostid}>{h.Host}</option>
                ))}
              </select>
            </div>

            <button className="pc-btn-crear" onClick={crearRelacion}>
              + Crear relación
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default PanelCorrelaciones;
